import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useCatalogoApi } from './api'
import { useAdminApi } from '../admin/api'
import { useAuth } from '../auth/useAuth'
import { tieneRol } from '../auth/roles'
import { t } from './i18n'
import { LangSelect, useLang } from '../i18n/LangProvider'
import { EquipoCard, EquipoResumen } from './EquipoCard'
import { t as tPanel, categoriaLabel } from '../panel/i18n'
import styles from './AppShell.module.css'
import { EmptyState } from '../styles/EmptyState'
import ukeLogo from '../assets/escudo-uke.png'
import { Icon } from '../styles/Icon'

interface Me {
  id: string
  email?: string
  roles?: string[]
  nombre_visible?: string
  idioma?: string
}

interface Temporada {
  id: string
  nombre: string
  estado: 'abierta' | 'cerrada'
}

export function AppShell() {
  const api = useCatalogoApi()
  const { logout, user } = useAuth()
  const [searchParams] = useSearchParams()
  const temporadaIdParam = searchParams.get('temporada_id')
  const tienePanel = tieneRol(user, 'admin', 'director', 'coordinador')
  const tieneAdmin = tieneRol(user, 'admin')
  const lang = useLang()
  const [menuOpen, setMenuOpen] = useState(false)
  const [me, setMe] = useState<Me | null>(null)
  const [temporadas, setTemporadas] = useState<Temporada[]>([])
  const [temporadaId, setTemporadaId] = useState<string>(temporadaIdParam ?? '')
  const [equipos, setEquipos] = useState<EquipoResumen[]>([])
  const [categoria, setCategoria] = useState<string>('')
  // Issue #90 (club, 2026-10-30): "el botón de Alertas debe cambiar a color verde cuando ya no queden
  // asistencias por actualizar". `null` mientras no se sabe (se ve como siempre).
  const [alertaPendiente, setAlertaPendiente] = useState<boolean | null>(null)

  useEffect(() => {
    api.get('/auth/me').then((data: Me) => {
      setMe(data)
    })
    api.get('/catalogo/temporadas').then((data: Temporada[]) => {
      setTemporadas(data)
      if (temporadaIdParam) return
      const abiertas = data.filter((temporada) => temporada.estado === 'abierta')
      if (abiertas.length === 1) setTemporadaId(abiertas[0].id)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporadaId) return
    api.get(`/catalogo/temporadas/${temporadaId}/equipos`).then((data: EquipoResumen[]) => {
      setEquipos(data)
      setCategoria((current) => (data.some((e) => e.categoria === current) ? current : (data[0]?.categoria ?? '')))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temporadaId])

  useEffect(() => {
    if (!temporadaId || !tienePanel) return
    api
      .get(`/panel/estado?temporada_id=${temporadaId}`)
      .then((estado: { semaforo: string }[]) => {
        if (Array.isArray(estado)) setAlertaPendiente(estado.some((e) => e.semaforo === 'rojo'))
      })
      .catch(() => setAlertaPendiente(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temporadaId, tienePanel])

  const estadoAlerta = alertaPendiente === null ? undefined : alertaPendiente ? 'pendiente' : 'ok'
  const iconoAlerta =
    alertaPendiente === false ? (
      <span aria-hidden className={styles.alertaOk}>
        ✓
      </span>
    ) : (
      <Icon name="alerta" className="icon-alerta" />
    )
  const tituloAlerta =
    alertaPendiente === null ? undefined : alertaPendiente ? tPanel('pendiente', lang) : tPanel('todoAlDia', lang)

  const categorias = Array.from(new Set(equipos.map((e) => e.categoria)))

  // Issue #79 (club, 2026-10-22): "permite al administrador arrastrar y soltar los paneles UKE Eskola,
  // F11 y F7 del lateral izquierdo, y que al entrar en cualquiera de ellos también se puedan reordenar
  // los equipos". Solo admin. El orden cambia en pantalla al soltar y se guarda en segundo plano; si
  // falla, se recarga el orden guardado. Arrastrar y soltar HTML5: escritorio (en táctil no hay drag).
  const adminApi = useAdminApi()
  const [arrastrando, setArrastrando] = useState<{ tipo: 'categoria' | 'equipo'; id: string } | null>(null)

  function mover<T>(lista: T[], desde: number, hasta: number): T[] {
    const copia = [...lista]
    const [item] = copia.splice(desde, 1)
    copia.splice(hasta, 0, item)
    return copia
  }

  async function recargarEquipos() {
    const data: EquipoResumen[] = await api.get(`/catalogo/temporadas/${temporadaId}/equipos`)
    setEquipos(data)
  }

  async function soltarCategoria(destino: string) {
    if (arrastrando?.tipo !== 'categoria' || arrastrando.id === destino) return setArrastrando(null)
    const nuevas = mover(categorias, categorias.indexOf(arrastrando.id), categorias.indexOf(destino))
    setArrastrando(null)
    setEquipos((prev) => [...prev].sort((a, b) => nuevas.indexOf(a.categoria) - nuevas.indexOf(b.categoria)))
    const resto = ['eskola', 'f7', 'f11'].filter((c) => !nuevas.includes(c))
    try {
      await adminApi.put('/admin/categorias/orden', { categorias: [...nuevas, ...resto] })
    } catch {
      await recargarEquipos()
    }
  }

  async function soltarEquipo(destinoId: string) {
    if (arrastrando?.tipo !== 'equipo' || arrastrando.id === destinoId) return setArrastrando(null)
    const ids = visibleEquipos.map((e) => e.id)
    const nuevos = mover(visibleEquipos, ids.indexOf(arrastrando.id), ids.indexOf(destinoId))
    setArrastrando(null)
    setEquipos((prev) => {
      let i = 0
      return prev.map((e) => (e.categoria === categoria ? nuevos[i++] : e))
    })
    try {
      await Promise.all(nuevos.map((e, orden) => adminApi.patch(`/admin/equipos/${e.id}`, { orden })))
    } catch {
      await recargarEquipos()
    }
  }

  const arrastrable = (tipo: 'categoria' | 'equipo', id: string, onDrop: () => void) =>
    tieneAdmin
      ? {
          draggable: true,
          onDragStart: (e: React.DragEvent) => {
            e.dataTransfer.effectAllowed = 'move'
            setArrastrando({ tipo, id })
          },
          onDragOver: (e: React.DragEvent) => {
            if (arrastrando?.tipo === tipo) e.preventDefault()
          },
          onDrop: (e: React.DragEvent) => {
            e.preventDefault()
            onDrop()
          },
          onDragEnd: () => setArrastrando(null),
          'data-arrastrando': arrastrando?.tipo === tipo && arrastrando.id === id ? 'true' : undefined,
          title: t('arrastrarParaOrdenar', lang),
        }
      : {}
  const visibleEquipos = equipos.filter((e) => e.categoria === categoria)

  return (
    <div className={styles.shell}>
      <header className={styles.topbar}>
        <button className={styles.hamburger} aria-label="ireki-menua" onClick={() => setMenuOpen(true)}>
          ☰
        </button>
        <Link className={styles.brand} to="/">
          <img className={styles.brandLogo} src={ukeLogo} alt="UKE Gara" />
          UKE Gara
        </Link>
        <div className={styles.topbarActions}>
          <LangSelect className={styles.langSelect} />
          {me && (
            <span className={styles.userBadge}>
              {me.nombre_visible ?? me.email} ({me.roles?.join(', ')})
            </span>
          )}
          {tienePanel && (
            <Link className={styles.panelLink} to="/panel" data-estado={estadoAlerta} title={tituloAlerta}>
              {iconoAlerta}
              {tPanel('panel', lang)}
            </Link>
          )}
          {tieneAdmin && (
            <Link className={styles.panelLink} to="/admin">
              {t('backoffice', lang)}
            </Link>
          )}
          {/* Petición del club (2026-10-31): manual de usuario accesible desde la app. */}
          <Link className={styles.panelLink} to="/ayuda" title={t('manualTitulo', lang)}>
            ❓ {t('ayuda', lang)}
          </Link>
          <button className={styles.logoutButton} onClick={() => logout()}>
            {t('salir', lang)}
          </button>
        </div>
      </header>

      {menuOpen && <div className={styles.overlay} onClick={() => setMenuOpen(false)} />}

      {temporadas.length > 0 && !temporadaId && (
        <select
          className={styles.temporadaSelect}
          value={temporadaId}
          onChange={(e) => setTemporadaId(e.target.value)}
        >
          <option value="" disabled>
            {t('seleccionaTemporada', lang)}
          </option>
          {temporadas.map((temporada) => (
            <option key={temporada.id} value={temporada.id}>
              {temporada.nombre}
              {temporada.estado === 'cerrada' ? ` (${t('cerrada', lang)})` : ''}
            </option>
          ))}
        </select>
      )}

      <div className={styles.body}>
        <nav className={`${styles.nav} ${menuOpen ? styles.navOpen : ''}`}>
          <div className={styles.drawerHeader}>
            <img className={styles.drawerLogo} src={ukeLogo} alt="UKE Gara" />
            {/* En móvil el badge de usuario no cabe en la topbar: va aquí (fix-mobile-responsive). */}
            {me && <span className={styles.drawerUser}>{me.nombre_visible ?? me.email}</span>}
            <button className={styles.closeButton} aria-label="itxi-menua" onClick={() => setMenuOpen(false)}>
              ✕
            </button>
          </div>
          {/* Look & feel (club, 2026-10-23: "este menú no está cambiado: f7, f11"): mismo estilo que el
              menú del backoffice — título de sección, punto de color por categoría y la activa en verde
              con relieve. */}
          <span className={styles.navSection}>{t('misEquipos', lang)}</span>
          {categorias.map((cat) => (
            <button
              key={cat}
              className={styles.navButton}
              onClick={() => {
                setCategoria(cat)
                setMenuOpen(false)
              }}
              // Issue #79: `aria-current` en vez de `disabled` para la activa — un botón deshabilitado no
              // se puede arrastrar.
              aria-current={cat === categoria ? 'true' : undefined}
              {...arrastrable('categoria', cat, () => soltarCategoria(cat))}
            >
              <span aria-hidden className={styles.catDot} data-categoria={cat} />
              {categoriaLabel(cat, lang)}
            </button>
          ))}
          {tienePanel && (
            <Link
              className={styles.drawerPanelLink}
              to="/panel"
              data-estado={estadoAlerta}
              title={tituloAlerta}
              onClick={() => setMenuOpen(false)}
            >
              {iconoAlerta}
              {tPanel('panel', lang)}
            </Link>
          )}
          {tieneAdmin && (
            <Link className={styles.drawerPanelLink} to="/admin" onClick={() => setMenuOpen(false)}>
              {t('backoffice', lang)}
            </Link>
          )}
          <Link className={styles.drawerPanelLink} to="/ayuda" onClick={() => setMenuOpen(false)}>
            ❓ {t('ayuda', lang)}
          </Link>
          <button className={styles.drawerLogoutButton} onClick={() => logout()}>
            {t('salir', lang)}
          </button>
        </nav>

        <main className={styles.main}>
          {temporadaId && visibleEquipos.length === 0 && <EmptyState text={t('sinEquipos', lang)} icon="🛡️" />}
          {visibleEquipos.map((equipo) => (
            <div key={equipo.id} className={styles.arrastrable} {...arrastrable('equipo', equipo.id, () => soltarEquipo(equipo.id))}>
              <EquipoCard equipo={equipo} lang={lang} />
            </div>
          ))}
        </main>
      </div>
    </div>
  )
}
